import { callFunction } from "../api/callFunction";
import UserAPI from "../api/UserApi";

export const MAP_CENTER = { lat: 37.68, lng: -122.18 };
// When the user explicitly centers the map on a point ("My location" / a custom
// pick), frame it so roughly this radius (miles) is visible — a neighborhood
// view, not the metro overview the map opens on.
export const FOCUS_RADIUS_MILES = 10;
export const MAP_OPTIONS = {
  disableDefaultUI: false,
  zoomControl: true,
  streetViewControl: false,
  mapTypeControl: false,
  fullscreenControl: true,
};
export const ORIGIN_STORAGE_KEY = "eventSuggestionsMapOrigin";

// Distinct teardrop pin for the currently-selected suggestion so it visibly
// pops out from the default red markers (bigger, brand-colored, white-outlined).
// Built lazily inside render because it needs window.google to be loaded.
export const SELECTED_PIN_PATH =
  "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z";
export const getSelectedMarkerIcon = () => ({
  path: SELECTED_PIN_PATH,
  fillColor: "#F09E7B",
  fillOpacity: 1,
  strokeColor: "#ffffff",
  strokeWeight: 2,
  scale: 2,
  anchor: new window.google.maps.Point(12, 22),
});

export const getDistanceMiles = (a, b) => {
  const toRad = (deg) => (deg * Math.PI) / 180;
  const R = 3958.8; // earth radius in miles
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
};

export const formatMiles = (mi) => {
  if (mi < 0.1) return "<0.1 mi";
  return mi < 10 ? `${mi.toFixed(1)} mi` : `${Math.round(mi)} mi`;
};

// --- Discover-style location detection -------------------------------------
// The map opens centered on the admin's own area instead of a hardcoded SF Bay
// default. The admin is always authenticated (host or superAdmin), so we reuse
// the tiered approach the PWA Discover page uses (see gist-pwa LocationAPI):
// their stored userLocations doc first, then an IP lookup — neither needs a
// geolocation permission prompt.

// Prefer a manually-set home (an explicit choice that should win), then the
// latest `current` slot (where they are now), then an IP-derived home — mirrors
// LocationAPI.anchorFromStored in the PWA. Works on both the stored doc and the
// resolveAndStoreLocation result, which share the { home, current } shape.
export const anchorFromStored = (stored) => {
  const home = stored?.home;
  if (home && home.source === "manual" && typeof home.lat === "number") return home;
  const current = stored?.current;
  if (current && typeof current.lat === "number") return current;
  return home && typeof home.lat === "number" ? home : null;
};

// Turn a stored location slot ({ lat, lng, city, state, ... }) into the
// { coords, label } shape the map origin expects.
export const slotToOrigin = (slot) => {
  if (!slot || typeof slot.lat !== "number" || typeof slot.lng !== "number") return null;
  const label = slot.city
    ? [slot.city, slot.state].filter(Boolean).join(", ")
    : "Detected location";
  return { coords: { lat: slot.lat, lng: slot.lng }, label };
};

// Read the caller's own userLocations/{uid} doc (owner-readable per rules).
export const readStoredLocation = async (uid) => {
  if (!uid) return null;
  try {
    return await UserAPI.getUserLocation(uid);
  } catch (err) {
    console.warn("[EventSuggestions] read stored location failed:", err);
    return null;
  }
};

// Ask the backend to resolve the caller's IP (it also stores it, matching the
// Discover fallback). Returns the { home, current } location or null.
export const resolveIpLocation = async () => {
  try {
    const res = await callFunction("resolveAndStoreLocation", {});
    return res?.location || null;
  } catch (err) {
    console.warn("[EventSuggestions] resolveAndStoreLocation failed:", err);
    return null;
  }
};

// Resolve the browser's precise position, but only if permission was already
// granted, so we never trigger a permission prompt on load. null on anything
// unsupported/denied/failed so the caller can fall back to IP detection.
export const getGrantedBrowserPosition = async () => {
  try {
    if (!navigator.geolocation || !navigator.permissions?.query) return null;
    const status = await navigator.permissions.query({ name: "geolocation" });
    if (status.state !== "granted") return null;
    return await new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => resolve(null),
        { enableHighAccuracy: false, timeout: 20000, maximumAge: 60000 }
      );
    });
  } catch {
    return null;
  }
};

export const getRelativeDate = (dateString) => {
  if (!dateString) return dateString;
  try {
    const date = dateString.toDate();
    if (isNaN(date.getTime())) return dateString;
    const diffDays = Math.round((date.getTime() - Date.now()) / 86400000);

    if (diffDays < -365) return `${Math.abs(Math.floor(diffDays / 365))}y ago`;
    if (diffDays < -30) return `${Math.abs(Math.floor(diffDays / 30))}mo ago`;
    if (diffDays < -7) return `${Math.abs(Math.floor(diffDays / 7))}w ago`;
    if (diffDays < 0) return `${Math.abs(diffDays)}d ago`;
    if (diffDays === 0) return "today";
    if (diffDays === 1) return "tomorrow";
    if (diffDays < 7) return `in ${diffDays}d`;
    if (diffDays < 30) return `in ${Math.floor(diffDays / 7)}w`;
    if (diffDays < 365) return `in ${Math.floor(diffDays / 30)}mo`;
    return `in ${Math.floor(diffDays / 365)}y`;
  } catch {
    return dateString;
  }
};

export const getRelativeDateColor = (dateString) => {
  if (!dateString) return "text-gray-400 dark:text-gray-500";
  try {
    const date = dateString.toDate();
    const diffDays = Math.round((date.getTime() - Date.now()) / 86400000);
    if (diffDays < 0) return "text-gray-400 dark:text-gray-500";
    if (diffDays <= 7) return "text-orange-500 dark:text-orange-400";
    if (diffDays <= 14) return "text-yellow-600 dark:text-yellow-400";
    return "text-green-600 dark:text-green-400";
  } catch {
    return "text-gray-400 dark:text-gray-500";
  }
};

export const getConfidenceBadge = (score) => {
  if (score == null) return { color: "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300" };
  if (score >= 0.7) return { color: "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300" };
  if (score >= 0.4) return { color: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200" };
  return { color: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300" };
};
